import React, { useState, useEffect, useMemo, useRef } from 'react';
import { mod, seededRandom } from './index';

// Whack-a-Mole: 15-second game. 3x3 grid. Mole positions and spawn times
// are deterministic from the shared seed so both players see the same
// sequence; only the per-side `moleLifetimeMs` differs based on DEX.

const GAME_DURATION_MS = 15000;
const SPAWN_COUNT      = 30;          // ~2 spawns/sec on average
const GRID_SIZE        = 3;            // 3x3 board

function lifetimeForDexDiff(myMod, theirMod) {
  // Equal DEX: 800ms. Each +2 modifier of advantage stretches your moles
  // by +100ms (cap +400ms). Each +2 of disadvantage shrinks by 100ms
  // (floor 350ms).
  const diff   = myMod - theirMod;        // can be negative
  const tiers  = Math.trunc(diff / 2);    // -N..+N
  let lifetime = 800 + tiers * 100;
  if (diff > 0) lifetime = Math.min(1200, lifetime); // cap +400ms
  if (diff < 0) lifetime = Math.max(350,  lifetime); // floor 350ms
  return lifetime;
}

export default {
  id: 'whackAMole',
  label: 'Whack-a-Mole',
  stat: 'dex',
  icon: '🦫',
  description: '15s game. Tap moles before they vanish. Higher DEX = your moles linger longer.',
  paramsFromStats(myScore, theirScore) {
    return { moleLifetimeMs: lifetimeForDexDiff(mod(myScore), mod(theirScore)) };
  },
  Component({ challenge, mySide, params, seed, onScore }) {
    const lifetimeMs = params?.moleLifetimeMs || 800;

    // Pre-compute the spawn schedule once. Same for both players (deterministic).
    const schedule = useMemo(() => {
      const rng = seededRandom(seed | 0);
      const out = [];
      // Spawn times spread across the game duration with jitter so they
      // don't clump or feel mechanical.
      const slot = GAME_DURATION_MS / SPAWN_COUNT;
      for (let i = 0; i < SPAWN_COUNT; i++) {
        const t   = Math.floor(i * slot + rng() * slot * 0.6);
        const cell = Math.floor(rng() * (GRID_SIZE * GRID_SIZE));
        out.push({ id: i, t, cell });
      }
      return out;
    }, [seed]);

    const startedAt = useRef(null);
    const [now, setNow]         = useState(0);
    const [score, setScore]     = useState(0);
    const [tapped, setTapped]   = useState(new Set());      // ids of moles already whacked
    const [done, setDone]       = useState(false);

    // Drive a render tick at ~60fps for smooth mole appearances/disappearances.
    useEffect(() => {
      startedAt.current = Date.now();
      let raf;
      const tick = () => {
        const elapsed = Date.now() - startedAt.current;
        if (elapsed >= GAME_DURATION_MS) {
          setNow(GAME_DURATION_MS);
          setDone(true);
          return;
        }
        setNow(elapsed);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, []);

    // Submit final score exactly once when the timer ends.
    const submitted = useRef(false);
    useEffect(() => {
      if (done && !submitted.current) {
        submitted.current = true;
        onScore(score);
      }
    }, [done, score, onScore]);

    // Active moles right now: those whose spawn time has passed and whose
    // lifetime hasn't expired AND that haven't been whacked yet.
    const active = schedule.filter((m) => {
      if (tapped.has(m.id)) return false;
      const age = now - m.t;
      return age >= 0 && age < lifetimeMs;
    });

    const cellHasMole = (cellIdx) => active.find((m) => m.cell === cellIdx);

    const whack = (cellIdx) => {
      const mole = cellHasMole(cellIdx);
      if (!mole) return;
      setTapped((prev) => {
        const next = new Set(prev);
        next.add(mole.id);
        return next;
      });
      setScore((s) => s + 1);
    };

    const remaining = Math.max(0, GAME_DURATION_MS - now);

    return (
      <div style={styles.wrap}>
        <div style={styles.title}>Whack-a-Mole</div>
        <div style={styles.headerRow}>
          <span style={styles.timer}>⏳ {(remaining / 1000).toFixed(1)}s</span>
          <span style={styles.score}>Score: {score}</span>
        </div>
        <div style={styles.grid}>
          {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, idx) => {
            const mole = cellHasMole(idx);
            const age  = mole ? now - mole.t : 0;
            // Fade the mole out as it ages so the player knows it's leaving.
            const fade = mole ? Math.max(0.4, 1 - age / lifetimeMs) : 0;
            return (
              <button
                key={idx}
                style={styles.cell}
                onClick={() => whack(idx)}
                onTouchStart={(e) => { e.preventDefault(); whack(idx); }}
              >
                {mole && (
                  <span style={{ ...styles.mole, opacity: fade }}>🦫</span>
                )}
              </button>
            );
          })}
        </div>
        {done && (
          <div style={styles.doneText}>
            Time! Final score: <strong>{score}</strong>. Submitting…
          </div>
        )}
      </div>
    );
  },
};

const styles = {
  wrap:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 16 },
  title:     { fontSize: 22, fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.06em' },
  headerRow: { display: 'flex', gap: 18, alignItems: 'center', fontSize: 14, fontWeight: 700, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace" },
  timer:     { background: '#0e1528', border: '1px solid #4a3d28', padding: '4px 10px', borderRadius: 4, color: '#9a8b6e' },
  score:     { background: 'rgba(212,168,71,0.15)', border: '1px solid #8b7340', padding: '4px 10px', borderRadius: 4, color: '#d4a847' },
  grid:      { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(3, 1fr)', gap: 8, width: 'min(360px, 80vw)', aspectRatio: '1' },
  cell:      { background: 'radial-gradient(circle at 50% 60%, #2a1a0e 0 35%, #1a0e08 50%, #0a0805 100%)', border: '2px solid #4a3d28', borderRadius: '50%', cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, overflow: 'hidden', minWidth: 0, minHeight: 0 },
  mole:      { fontSize: 'clamp(24px, 7vw, 44px)', lineHeight: 1, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))', pointerEvents: 'none' },
  doneText:  { fontSize: 13, color: '#d4a847', fontFamily: "'Press Start 2P', monospace" },
};
